import { pipe } from 'fp-ts/function';
import { array } from 'fp-ts';
import { useTheme } from 'styled-components';
import { VectorDirection } from '@mews/enums';
import { useAppClockService } from '@mews/framework';
import { Empty } from '@mews-commander/ui';
import { useCommanderSelector } from '@mews-commander/utils';
import { ASIDE_WIDTH, CELL_WIDTH, HEADER_HEIGHT } from '../../constants';
import { getDateRangeArray } from '../../selectors';
import { getCellHeightsByRow } from '../../utils';
import { RestrictionCalendarRow } from '../../types';
import { RestrictionHeaderCellElement, RestrictionsMatrixElement } from './styles';
import { RestrictionsAsideCell } from './cells';
import RestrictionsMatrixVector from './RestrictionsMatrixVector';

interface Props {
    isMobile?: boolean;
    serviceId: string;
    restrictionCalendarRows: Array<RestrictionCalendarRow>;
}

const RestrictionsMatrix = ({ isMobile, serviceId, restrictionCalendarRows }: Props) => {
    const hasServiceRestrictions = pipe(restrictionCalendarRows, array.isNonEmpty);
    const appClockService = useAppClockService();
    const dateRangeArray = useCommanderSelector((state) => getDateRangeArray(state, { serviceId, appClockService }));
    const maxExceptionCountByRows = restrictionCalendarRows.map((row) => row.maxExceptionsCount);
    const hoursIntervalCount = restrictionCalendarRows.map((row) => row.hoursIntervalCount);
    const theme = useTheme();

    const renderAsideCells = array.mapWithIndex((rowIndex, row: RestrictionCalendarRow) => (
        <RestrictionsAsideCell
            rowIndex={rowIndex}
            key={`restrictions-aside-cell-${rowIndex}`}
            serviceId={serviceId}
            restrictionCalendarRows={restrictionCalendarRows}
            type={row.conditions.type}
        />
    ));

    const renderMatrixVector = array.mapWithIndex((rowIndex, row: RestrictionCalendarRow) => (
        <RestrictionsMatrixVector
            key={`restrictions-vector-${rowIndex}`}
            // @ts-expect-error $TSFixMeError: Type '{ key: string; vectorRestrictions: any; rowIndex: any; serviceId: any; }' is not assignable to type 'IntrinsicAttr...
            vectorRestrictions={row.restrictions}
            rowIndex={rowIndex}
            serviceId={serviceId}
            conditions={row.conditions}
        />
    ));

    return hasServiceRestrictions ? (
        <RestrictionsMatrixElement
            asideCells={renderAsideCells(restrictionCalendarRows)}
            asideWidth={ASIDE_WIDTH}
            cellHeight={getCellHeightsByRow(maxExceptionCountByRows, hoursIntervalCount, theme)}
            cellWidth={CELL_WIDTH}
            disableScrollSync={isMobile}
            headerHeight={HEADER_HEIGHT}
            headerCells={dateRangeArray.map((date) => (
                <RestrictionHeaderCellElement key={`restrictions-header-cell-${date.value}`} date={date} />
            ))}
            headerProps={{ isMobile }}
            vectorDirection={VectorDirection.Row}
            vectors={renderMatrixVector(restrictionCalendarRows)}
        />
    ) : (
        <Empty />
    );
};

export default RestrictionsMatrix;
